/**
 * Sessie 169: een PDF achter de admin-login openen (label, pakbon).
 *
 * De PDF komt als blob via apiClient (Bearer), nooit als kale href. Op
 * iOS Safari mag een venster alleen synchroon bij de tik open; na een
 * `await` blokkeert Safari window.open. Daarom: eerst bij de tik een leeg
 * venster openen, daarna de object-URL erin zetten. Lukt window.open
 * niet (null), dan een anchor met download-attribuut.
 *
 * De object-URL blijft ruim een minuut geldig: een nieuw tabblad laadt
 * hem pas na het openen, en direct intrekken geeft een lege pagina.
 */
import type { AxiosError } from "axios";

import { vertaalApiFout } from "@/lib/api";

/** Hoe lang de object-URL blijft bestaan (ms). */
export const INTREK_NA_MS = 10 * 60_000;

function downloadViaAnker(url: string, bestandsnaam: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = bestandsnaam;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function blobTekst(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((klaar, mis) => {
    const lezer = new FileReader();
    lezer.onload = () => klaar(String(lezer.result ?? ""));
    lezer.onerror = () => mis(lezer.error);
    lezer.readAsText(blob);
  });
}

/**
 * Leest de fouttekst uit een blob-antwoord. Bij responseType "blob" is
 * ook de foutbody een Blob, dus vertaalApiFout ziet het detail niet.
 */
export async function pdfFoutTekst(
  fout: unknown,
  vangnet: string,
): Promise<string> {
  const ax = fout as AxiosError<unknown>;
  const data = ax?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const tekst = await blobTekst(data);
      const json = JSON.parse(tekst) as { detail?: unknown };
      if (typeof json.detail === "string" && json.detail.trim()) {
        return json.detail;
      }
    } catch {
      // Geen JSON: val terug op de status hieronder.
    }
    const status = ax.response?.status;
    if (status === 404) return `${vangnet} (niet gevonden)`;
    if (status === 502) return `${vangnet} (de vervoerder gaf hem niet)`;
    return vangnet;
  }
  return vertaalApiFout(fout, vangnet);
}

/**
 * Open een PDF uit een blob-belofte. Roep dit aan direct in de
 * tik-handler (niet na een await), anders werkt het venster op iOS niet.
 * Gooit de fout door met een leesbare tekst als `Error.message`.
 */
export async function openPdf(
  ophalen: () => Promise<Blob>,
  bestandsnaam: string,
  vangnet = "De PDF kon niet worden geopend",
): Promise<void> {
  // Synchroon bij de tik, vóór elke await.
  let venster: Window | null;
  try {
    venster = window.open("", "_blank");
  } catch {
    venster = null;
  }
  let blob: Blob;
  try {
    blob = await ophalen();
  } catch (fout) {
    if (venster) venster.close();
    // De lib-instelling van tsc -b kent de cause-optie van Error niet;
    // de reden zit al in de tekst (pdfFoutTekst leest de fout uit).
    // eslint-disable-next-line preserve-caught-error
    throw new Error(await pdfFoutTekst(fout, vangnet));
  }
  const pdf =
    blob.type === "application/pdf"
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  const url = URL.createObjectURL(pdf);
  if (venster) {
    venster.location.href = url;
  } else {
    downloadViaAnker(url, bestandsnaam);
  }
  window.setTimeout(() => URL.revokeObjectURL(url), INTREK_NA_MS);
}
